import React, { createRef } from "react";
import BaseElem from "../BaseElem";
import "../Common.css";
import "./NursingProcedures.css";
import { TextDropdown } from "./TextDropdown";
import { c7C7C7C, UNDEF, c4A4A4A } from "../Constants";
import { blur, clamp, eatClick, isBlank } from "Util/Utilities";
import { Div, Img, Txt } from "./GenericView";

type NursingProceduresProps = {
  available_procedures?: any;
  selectedProcedures?: any[];

  className?: string;
  tdClassName?: string;
  tdInputClassName?: string;

  // disable all inputs for readonly state
  readOnly?: boolean;

  //hidden?: boolean
  dontAnimate?: boolean;

  showApplyAll?: boolean;
  doneBtnText?: string;

  style?: any;

  noCounter?: boolean;

  onProceduresChange?: Function;

  dontDisableDocScroll?: boolean;

  placeholder?: string;

  // is this home medical procedures
  homeMedical?: boolean;
};

type NursingProceduresState = {
  _tempProcedures?: any[];
};

export class NursingProcedures extends BaseElem<
  NursingProceduresProps,
  NursingProceduresState
> {
  refProceduresDropdown = createRef<TextDropdown>();
  refOpenProceduresBtn = createRef<HTMLDivElement>();
  _procedures: any[] = [];

  constructor(props: NursingProceduresProps) {
    super(props);
    this.state = {};
  }

  // Component maintains an internal state, call cleanup to clear
  _cleanup() {
    this.setState({
      _tempProcedures: UNDEF,
    });
  }

  _getProcedures() {
    const self = this;
    const existing = self._procedures;
    const procedures: any = self.props.available_procedures;

    if (!isBlank(existing)) return existing;

    if (self.props.homeMedical) {
      self._procedures = procedures.map((p: any) => ({
        name: p.name,
        quantity: 1,
        consumables: p.consumables,
      }));
      return self._procedures;
    }

    const simpleNursing = procedures?.simple_nursing_procedures || [];
    const complexNursing = procedures?.complex_nursing_procedures || [];

    if (!procedures || isBlank(simpleNursing) || isBlank(complexNursing))
      return [];

    const header1 = [{ type: "header", name: "Nursing (Simple)" }];
    const divider = [{ type: "divider", name: "", key: "d1" }];
    const header2 = [{ type: "header", name: "Nursing (Complex)" }];

    const proceduresList = header1
      .concat(
        simpleNursing.map((v: any) => {
          return {
            name: v.name || v,
            type: "Simple",
            quantity: 1,
            consumables: v.consumables,
          };
        })
      )
      .concat(divider)
      .concat(header2)
      .concat(
        complexNursing.map((v: any) => {
          return {
            name: v.name || v,
            type: "Complex",
            quantity: 1,
            consumables: v.consumables,
          };
        })
      );

    self._procedures = proceduresList;
    return proceduresList;
  }

  _toggleConsumable(proc: any, consumable: string, dropdown: TextDropdown) {
    if (!proc) return;

    let list = proc.consumables || [];
    if (list.includes(consumable)) {
      list = list.filter((l: string) => l !== consumable);
    } else {
      list.push(consumable);
    }
    proc.consumables = list;

    dropdown.forceUpdate();
  }

  _updateCount(proc: any, increment = false) {
    const self = this,
      props = self.props;
    proc.quantity = clamp(parseInt(proc.quantity) + (increment ? 1 : -1), 1, 9);

    const onChangeCallback = props.onProceduresChange;
    onChangeCallback && onChangeCallback(props.selectedProcedures);
  }

  _deleteProc(proc: any) {
    const self = this,
      props = self.props;
    let procedures = (props.selectedProcedures || []).filter(
      (p: any) => p.name !== proc.name
    );

    const onChangeCallback = props.onProceduresChange;
    onChangeCallback && onChangeCallback(procedures);
  }

  // new procedure selected from dropdown
  _newProcedureSelected = (dropdown: TextDropdown, procedure: any) => {
    if (!procedure) return;
    const self = this;
    let list = self.state._tempProcedures || [
      ...(self.props.selectedProcedures || []),
    ];

    const procCopy = Object.assign({}, procedure);
    delete procCopy.consumables;

    const idx = list.findIndex((item: any) => item.name === procedure.name);
    if (idx >= 0) {
      list.splice(idx, 1);
    } else {
      list.push(procCopy);

      // Auto scroll into view when there are consumables
      if (!isBlank(procedure.consumables)) {
        dropdown.async(
          () => dropdown.scrollDropdownToSelectedItem(UNDEF, procedure),
          100
        );
      }
    }

    self.setState({
      _tempProcedures: list,
    });
  };

  _openProceduresDropdown = () => {
    this.refProceduresDropdown.current?.focus();
  };

  // procedures popup open callback
  _onProcPopupOpen = (_inpField: any) => {
    this.toggleClass("gone", this.refOpenProceduresBtn?.current, true);
  };

  // copy temporary procedures list back to visit
  _onProcPopupClose(inpField: any, isApplyAll = false) {
    const self = this,
      props = self.props;

    let procedures = (
      self.state._tempProcedures ||
      props.selectedProcedures ||
      []
    ).slice();

    // convert to plain array
    if (props.noCounter) {
      procedures = procedures.map((p) => p.name);
    }

    const onChangeCallback = props.onProceduresChange;
    onChangeCallback && onChangeCallback(procedures, isApplyAll);

    if (inpField) {
      self.toggleClass("gone", self.refOpenProceduresBtn?.current, false);
      inpField.clearValue();
    }
    self._cleanup();
  }

  // Consumables section
  _subItemRender = (item: any, isSelected: boolean, dropdown: TextDropdown) => {
    const consumables = item.consumables;
    if (!isSelected || !consumables || !consumables[0]) return;

    const list =
      this.state._tempProcedures || this.props.selectedProcedures || [];
    const proc = list.find((i) => i.name === item.name);
    const selectedConsumables = (proc && proc.consumables) || [];

    return (
      <Div v g pb={6}>
        <span className="npc-consumable-lbl">
          Any consumables to add for this procedure?
        </span>
        <Div className="h w">
          {consumables.map((c: string) => (
            <Txt
              key={c}
              className="npc-consumable"
              isSelected={selectedConsumables.includes(c)}
              onClick={(e: any) =>
                eatClick(e) && this._toggleConsumable(proc, c, dropdown)
              }
            >
              {c}
            </Txt>
          ))}
        </Div>
      </Div>
    );
  };

  // Additional info for consumables
  _infoRender = () => {
    const props = this.props;
    const procedures =
      this.state._tempProcedures || props.selectedProcedures || [];
    // Only show the info section when some consumables are selected
    if (procedures.some((p) => !isBlank(p.consumables))) {
      const cls = props.showApplyAll ? " npc-no-float" : "";
      return (
        <div className={"npc-info-consumables h vc" + cls}>
          <Img size={12} src="/self-serve/info.png" mr={6} />
          The price of the consumables will be confirmed later.
        </div>
      );
    }
  };

  render() {
    const self = this,
      state = self.state,
      props = self.props,
      isViewOnly = props.readOnly,
      viewOnlyGone = isViewOnly ? " gone" : "", // hide for readonly mode
      showCounter = !props.noCounter,
      selectedProcedures = (props.selectedProcedures || []).slice();

    return (
      <div className={`${props.className} v`} style={props.style}>
        {showCounter && (
          <Div v w100>
            {selectedProcedures.map((item: any) => {
              const name = item.name || item;
              const quantity = item.quantity || 1;
              const consumables = item.consumables || [];

              return (
                <div key={name} className="v npc-procedure-row">
                  <div className="h center">
                    <span className="grow">{name}</span>
                    <input
                      type="image"
                      className={
                        "npc-counter npc-ml8" +
                        viewOnlyGone +
                        (quantity <= 1 ? " disable" : "")
                      }
                      alt=""
                      src="/dec.png"
                      onClick={(e: any) =>
                        blur(e) && self._updateCount(item, false)
                      }
                    />
                    <span className="npc-quant">{quantity}</span>
                    <input
                      type="image"
                      className={
                        "npc-counter" +
                        viewOnlyGone +
                        (quantity >= 9 ? " disable" : "")
                      }
                      alt=""
                      src="/inc.png"
                      onClick={(e: any) =>
                        blur(e) && self._updateCount(item, true)
                      }
                    />
                    <input
                      type="image"
                      className={"npc-delete" + viewOnlyGone}
                      alt=""
                      src="/delete.png"
                      onClick={() => self._deleteProc(item)}
                    />
                  </div>

                  {!isBlank(consumables) && (
                    <Txt color={c7C7C7C} fs={12} mt={4} lineHeight="16px">
                      Consumables: {consumables.join(", ")}
                    </Txt>
                  )}
                </div>
              );
            })}
          </Div>
        )}

        {/* Show chips */}
        {!showCounter && (
          <Div className="h wrap" mt={-3}>
            {selectedProcedures.map((item: any) => {
              return (
                <div
                  className={"chip" + (isViewOnly ? " chip-no-cross" : "")}
                  key={item.name}
                >
                  {item.name}
                  <img
                    tabIndex={0}
                    src="/close.svg"
                    alt=""
                    className="chip-delete"
                    onClick={() => self._deleteProc(item)}
                    onKeyUp={(e: any) =>
                      e.key === "Enter" && self._deleteProc(item)
                    }
                  />
                </div>
              );
            })}
          </Div>
        )}

        <div
          hidden={isViewOnly}
          ref={self.refOpenProceduresBtn}
          className="npc-open-proc"
          onClick={self._openProceduresDropdown}
        >
          + Add a service
        </div>

        <TextDropdown
          hidden={isViewOnly}
          ref={self.refProceduresDropdown}
          className={props.tdClassName}
          inputClassName={props.tdInputClassName}
          width="100%"
          maxWidth="100%"
          popupWidth="441px"
          popupHeight="307px"
          dontAnimate={props.dontAnimate}
          items={self._getProcedures()}
          multiSelection
          variableRowHeight={showCounter}
          showCounter={showCounter}
          showSelectionTick={!showCounter}
          titleProperty="name"
          dropdownColor={c4A4A4A}
          placeholder={props.placeholder || "Select nursing procedure"}
          subItemRender={showCounter ? self._subItemRender : UNDEF}
          infoRender={showCounter ? self._infoRender : UNDEF}
          doneBtnText={props.doneBtnText}
          dontDisableDocScroll={props.dontDisableDocScroll}
          selectedItems={state._tempProcedures || selectedProcedures}
          onChange={self._newProcedureSelected}
          onFocus={self._onProcPopupOpen}
          onBlur={(comp: any) => self._onProcPopupClose(comp)}
          onApplyAll={
            !props.showApplyAll
              ? UNDEF
              : () => self._onProcPopupClose(UNDEF, true)
          }
        />
      </div>
    );
  }
}
